import { useState } from 'react';
import { useI18n } from '../i18n';
import type { AppRoute } from '../routes';
import { AdaptiveSheet } from '../components/AdaptiveSheet';
import { exportJobKindLabel, exportJobStatusLabel } from '../features/exports/job-presentation';
import { useTaskSystem } from './TaskProvider';
import type { UiTask } from './task-model';

function statusLabel(task: UiTask, zh: boolean): string {
  if (task.remoteJob) return exportJobStatusLabel(task.remoteJob.status, zh);
  if (task.status === 'queued') return zh ? '等待中' : 'Queued';
  if (task.status === 'running') return zh ? '进行中' : 'Running';
  if (task.status === 'complete') return zh ? '已完成' : 'Complete';
  return zh ? '需要处理' : 'Needs attention';
}

function stageLabel(task: UiTask, zh: boolean): string {
  if (!task.remoteJob) return task.stage;
  const stage = task.remoteJob.progressStage || task.remoteJob.status;
  const labels: Record<string, [string, string]> = {
    queued: ['等待开始', 'Queued'],
    collecting: ['收集数据', 'Collecting'],
    snapshot: ['冻结快照', 'Snapshot'],
    rendering: ['生成文件', 'Rendering'],
    packaging_media: ['打包媒体', 'Packaging media'],
    checksum: ['校验文件', 'Checksum'],
    complete: ['已完成', 'Complete'],
    failed: ['需要处理', 'Needs attention'],
    expired: ['已过期', 'Expired']
  };
  return labels[stage]?.[zh ? 0 : 1] ?? stage;
}

function taskKind(task: UiTask, zh: boolean): string {
  return task.remoteJob ? exportJobKindLabel(task.remoteJob, zh) : (zh ? '后台任务' : 'Background task');
}

export function TaskCenter({ navigate, compact = false }: {
  navigate: (route: AppRoute) => void;
  compact?: boolean;
}) {
  const { locale } = useI18n();
  const zh = locale === 'zh-CN';
  const { remoteJobsEnabled, tasks, counts, loading, error, refresh, retry } = useTaskSystem();
  const [open, setOpen] = useState(false);
  const [retryingId, setRetryingId] = useState<string | null>(null);

  const badge = counts.attention || counts.active;

  async function retryTask(task: UiTask) {
    if (retryingId) return;
    setRetryingId(task.id);
    try { await retry(task); }
    finally { setRetryingId(null); }
  }

  return <>
    <button type="button" className={`task-center-trigger ${compact ? 'compact' : ''}`} aria-haspopup="dialog"
      aria-expanded={open} onClick={() => setOpen(true)}>
      <span aria-hidden="true">◌</span>
      {!compact && <em>{zh ? '任务' : 'Tasks'}</em>}
      {badge > 0 && <strong className={counts.attention ? 'attention' : ''}>{badge > 99 ? '99+' : badge}</strong>}
    </button>
    <AdaptiveSheet open={open} eyebrow="Tasks" title={zh ? '任务中心' : 'Task Center'} closeLabel={zh ? '关闭任务中心' : 'Close Task Center'} onDismiss={() => setOpen(false)}
      footer={<div className="task-center-footer">
        <button type="button" className="text-button" disabled={loading} onClick={() => void refresh()}>{zh ? '刷新' : 'Refresh'}</button>
        {remoteJobsEnabled && <button type="button" className="secondary-button" onClick={() => { setOpen(false); navigate({ screen: 'exports' }); }}>{zh ? '打开导出中心' : 'Open Export Center'}</button>}
      </div>}>
      <div className="task-center-summary">
        <span><strong>{counts.active}</strong>{zh ? '进行中' : ' active'}</span>
        <span className={counts.attention ? 'attention' : ''}><strong>{counts.attention}</strong>{zh ? '需要处理' : ' attention'}</span>
      </div>
      {error && <p className="task-center-error" role="alert">{zh ? '任务状态暂时无法刷新，已有任务记录不会丢失。' : 'Task status could not refresh. Existing job records are retained.'}</p>}
      {loading && !tasks.length && <p className="muted-note" role="status">{zh ? '正在读取后台任务…' : 'Loading background tasks…'}</p>}
      {!loading && !tasks.length && <p className="empty-state">{zh ? '目前没有后台任务。' : 'No background tasks yet.'}</p>}
      <div className="task-center-list">
        {tasks.slice(0, 20).map(task => <article className={`task-center-item ${task.status}`} key={task.id}>
          <div className="task-center-item-head">
            <div><small>{taskKind(task, zh)}</small><strong>{task.title}</strong></div>
            <span>{statusLabel(task, zh)}</span>
          </div>
          <div className="task-center-progress" aria-label={`${statusLabel(task, zh)} ${task.progress}%`}><span style={{ width: `${task.progress}%` }} /></div>
          <div className="task-center-meta"><span>{task.progress}%</span><span>{stageLabel(task, zh)}</span><span>{new Date(task.updatedAt).toLocaleString()}</span></div>
          {task.status === 'failed' && <div className="task-center-attention">
            <p>{task.errorMessage || (zh ? '这项任务没有完成，记录仍保留。' : 'This task did not complete; its record is retained.')}</p>
            {task.retryable && <button type="button" className="text-button" disabled={Boolean(retryingId)}
              onClick={() => { void retryTask(task); }}>{retryingId === task.id ? (zh ? '正在重试…' : 'Retrying…') : (zh ? '重试' : 'Retry')}</button>}
          </div>}
        </article>)}
      </div>
    </AdaptiveSheet>
  </>;
}
